import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { getTranslation } from '../config/translations';
import serverClient from '../api/ServerClient';
import { TOKEN_TAG } from '../config/app.config';

// Create Language Context
const LanguageContext = createContext();

// Helper function to set language
const setLanguageHelper = (lang) => {
    document.documentElement.lang = lang;
};

// Language Provider Component
export const LanguageProvider = ({ children }) => {
    const [language, setLanguage] = useState(() => {
        return localStorage.getItem('language') || 'en';
    });

    // Fetch language settings from API
    const fetchLanguageSettings = useCallback(async (forceRefresh = false) => {
        try {
            // If not forcing refresh, check localStorage first
            if (!forceRefresh) {
                const savedLanguage = localStorage.getItem('language');
                if (savedLanguage) {
                    setLanguage(savedLanguage);
                    return;
                }
            }

            // Fetch from API
            const response = await serverClient.get('/api/user/settings');
            const apiLanguage = response?.data?.settings?.language || 'en';

            setLanguage(apiLanguage);
            localStorage.setItem('language', apiLanguage);
        } catch (error) {
            // Silently default to 'en' on error
            setLanguage('en');
            localStorage.setItem('language', 'en');
        }
    }, []);

    // Listen for login and set up initial language
    useEffect(() => {
        const handleStorageChange = (e) => {
            // Detect when token is set (user logged in)
            if (e.key === TOKEN_TAG && e.newValue) {
                fetchLanguageSettings(true); // Force refresh to get fresh settings from API after login
            }
        };

        // Check if user is already logged in on mount
        const token = localStorage.getItem(TOKEN_TAG);
        if (token && token.length > 10) {
            fetchLanguageSettings(false); // Use cached language on mount if available
        }

        // Listen for storage changes
        window.addEventListener('storage', handleStorageChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [fetchLanguageSettings]);

    // Set HTML lang attribute when language changes
    useEffect(() => {
        setLanguageHelper(language);
    }, [language]);

    // Update language and persist to localStorage
    const changeLanguage = useCallback((newLanguage) => {
        setLanguage(newLanguage);
        localStorage.setItem('language', newLanguage);
        setLanguageHelper(newLanguage);

        // Dispatch event for components listening to language changes
        window.dispatchEvent(
            new CustomEvent('languageChange', {
                detail: { language: newLanguage },
            })
        );
    }, []);

    // Translation function
    const t = useCallback(
        (key) => {
            return getTranslation(key, language);
        },
        [language]
    );

    // Memoize context value to prevent unnecessary re-renders
    const value = useMemo(
        () => ({
            language,
            changeLanguage,
            t,
            fetchLanguageSettings,
        }),
        [language, changeLanguage, t, fetchLanguageSettings]
    );

    return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

// Custom hook to use Language Context
export const useLanguage = () => {
    const context = useContext(LanguageContext);

    if (!context) {
        throw new Error('useLanguage must be used within LanguageProvider');
    }

    return context;
};
